import { Typography } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import { Box } from '@mui/system';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import NotFound from '../components/NotFound/NotFoundPage';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import {
  handleTimeFilter,
  handleStatusFilter,
  searchDataByKey,
  allergiesArrayConvertor,
  extractValueFromCoding,
  getIconFromSubItemsArray,
  createReactionsArray,
  getStatusFilterParamList,
  calculateDateFilterTimeStamp,
} from '../shared/helper';
import {
  allergiesStatusFilterDatas,
  DEFAULT_NULL_REPLACEMENT,
  dateFilterMenuItems,
  NUMBER_OF_ROWS_PER_PAGE,
  ALLERGIES_STATUS_LIST,
} from '../shared/constants';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import {
  FETCH_ALL_ALLERGIES_QUERY,
  FETCH_ALLERGY_DETAILS_BY_ID_QUERY,
} from '../services/AllergiesQueries/AllergiesService';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import ApplicationContext from '../context/applicationContext';
import {
  ALLERGIES,
  ALLERGIES_TABLE_HEADING as tableHeading,
  ALLERGIES_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

export const Allergies = () => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailsData, setDetailsData] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();

  // const allAllergies = useQuery(FETCH_ALL_ALLERGIES_QUERY, {
  //   variables: {
  //     sourceUrl: '',
  //     count: '10',
  //     offset: 0,
  //     dateFilter: 0,
  //     statusFilter: '',
  //     searchFilter: '',
  //   },
  // });

  const [fetchAllAllergies, fetchAllAllergiesCall] = useLazyQuery(
    FETCH_ALL_ALLERGIES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(ALLERGIES.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        let result = res.fetchAllAllergies.allergyList;
        let convetor = JSON.parse(result);
        let data = allergiesArrayConvertor(convetor);
        setRecordedData(data);
        setFilteredData(data);
        setTotalCount(res.fetchAllAllergies.count);
      },
    }
  );

  const [fetchAllergyById, fetchAllergiesByIdCall] = useLazyQuery(
    FETCH_ALLERGY_DETAILS_BY_ID_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(ALLERGIES.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchAllergyById);
      },
    }
  );

  useEffect(() => {
    fetchAllAllergies({
      variables: {
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
        statusFilter: getStatusFilterParamList(
          statusFilter,
          allergiesStatusFilterDatas,
          ALLERGIES_STATUS_LIST
        ),
        searchFilter: searchValue,
      },
    });
  }, [offset, searchValue, timeFilter, statusFilter, fetchAllAllergies]);

  const handleChangeFilter = () => {
    setOffset(0);
  };

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: allergiesStatusFilterDatas,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const showFlyout = result => {
    const parseResult = JSON.parse(result?.allergy);
    //console.log(JSON.parse(result?.allergy));

    const tableRowInfo = recordsData.find(obj => obj.id === selectedTableId);
    const modalData = [
      {
        column_name: ALLERGIES.MODAL_DATA.STATUS,
        value: tableRowInfo.status,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
        width: 6,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.RECORDED_BY,
        value: parseResult?.recorder?.display,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.FACILITY,
        value:
          parseResult?.encounter?.display
            .replaceAll('[', '')
            .replaceAll(']', '') || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.CODE,
        value: parseResult?.code.coding[0].code || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.CRITICALITY,
        value: parseResult?.criticality || DEFAULT_NULL_REPLACEMENT,
        capitalize: true,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.CATEGORY,
        value: parseResult?.category[0] || DEFAULT_NULL_REPLACEMENT,
        capitalize: true,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.ROUTE,
        value:
          parseResult?.reaction && parseResult?.reaction.length
            ? extractValueFromCoding(parseResult?.reaction[0].exposureRoute)
            : DEFAULT_NULL_REPLACEMENT,
        capitalize: true,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.REACTION,
        value: createReactionsArray(parseResult?.reaction),
      },
      {
        column_name: ALLERGIES.MODAL_DATA.NOTE,
        value:
          parseResult?.reaction?.[0]?.note?.[0]?.text ||
          DEFAULT_NULL_REPLACEMENT,
        width: 12,
        capitalize: true,
      },
      {
        column_name: ALLERGIES.MODAL_DATA.DOCUMENTS,
        value: result?.docArn[0] || DEFAULT_NULL_REPLACEMENT,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.allergicTo);
    fetchAllergyById({
      variables: {
        allergyId: id,
      },
    });
  };

  // useEffect(() => {
  //   const timeFilteredData = handleTimeFilter(
  //     timeFilter,
  //     recordsData,
  //     'recordedOn'
  //   );
  //   const statusFilteredData = handleStatusFilter(
  //     statusFilter,
  //     timeFilteredData,
  //     allergiesStatusFilterDatas
  //   );
  //   const searchData = searchDataByKey(
  //     statusFilteredData,
  //     'allergicTo',
  //     searchValue
  //   );
  //   setFilteredData(searchData);
  // }, [timeFilter, statusFilter, searchValue, recordsData]);
  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{ALLERGIES.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={ALLERGIES.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>

      <Box sx={table}>
        {!fetchAllAllergiesCall.loading && !filteredData.length ? (
          <NotFound title={ALLERGIES.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Allergies'
              )
            : null
        }
        category={ALLERGIES.TITLE_TEXT}
        title={modalTitle}></ServiceFlyout>
      <SimpleBackdrop
        open={fetchAllAllergiesCall.loading || fetchAllergiesByIdCall.loading}
      />
    </Content>
  );
};
